import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Typography } from '@material-ui/core';
import Forward from '@material-ui/icons/Forward';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';

import compose from 'helpers/compose';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import FormTextField from 'components/FormFields/FormTextField';
import Background from 'components/Layout/Background';
import FormDatePicker from 'components/FormFields/FormDatePicker';

import { AdminCsvStore, initialValues, OrderType } from 'stores/admin/adminCsvStore';
import { RootStore } from 'stores/rootStore';

import { Permission } from 'helpers/permissions';

interface Props {
  rootStore: RootStore;
}

const Csv: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new AdminCsvStore(rootStore));
  const { successDialog, confirmDialog } = store;
  const admin = rootStore.stores.userStore.checkPermissions(Permission.ACCESS_ADMIN);

  if (!store) return null;

  return (
    <Container maxWidth="md">
      <Background inside>
        <Typography variant="h1">{t('backOffice_csv_title')}</Typography>
        <Formik initialValues={initialValues} onSubmit={() => {}}>
          {(formikProps) => (
            <Grid container>
              <Grid item xs={12} container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    <strong>{t('backOffice_transfers_title')}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <FormTextField name="transfer" label={t('backOffice_transferIDs')} margin="dense" maxLength={1000} />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => store.handleOrder(OrderType.TRANSFER, formikProps.values.transfer)}
                  >
                    {t('backOffice_uploadIDs')}
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button variant="contained" color="primary" onClick={() => store.downloadOrder(OrderType.TRANSFER)}>
                    {t('general_generate')}
                  </Button>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => store.showConfirmDialog(OrderType.TRANSFER)}
                  >
                    <Forward />
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    <strong>{t('backOffice_withdrawals_title')}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <FormTextField
                    name="withdrawal"
                    label={t('backOffice_withdrawalIDs')}
                    margin="dense"
                    maxLength={1000}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => store.handleOrder(OrderType.WITHDRAWAL, formikProps.values.withdrawal)}
                  >
                    {t('backOffice_uploadIDs')}
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button variant="contained" color="primary" onClick={() => store.downloadOrder(OrderType.WITHDRAWAL)}>
                    {t('general_generate')}
                  </Button>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => store.showConfirmDialog(OrderType.WITHDRAWAL)}
                  >
                    <Forward />
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    <strong>{t('backOffice_fees_title')}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <FormTextField name="fee" label={t('backOffice_feeIDs')} margin="dense" maxLength={1000} />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => store.handleOrder(OrderType.FEE, formikProps.values.fee)}
                  >
                    {t('backOffice_uploadIDs')}
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  <Button variant="contained" color="primary" onClick={() => store.downloadOrder(OrderType.FEE)}>
                    {t('general_generate')}
                  </Button>
                </Grid>
                <Grid item xs={1}>
                  <Button variant="contained" color="primary" onClick={() => store.showConfirmDialog(OrderType.FEE)}>
                    <Forward />
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={2}>
                  <Typography>
                    <strong>{t('backOffice_commission_title')}</strong>
                  </Typography>
                </Grid>
                <Grid container item xs={5} spacing={0} alignItems="center">
                  <Grid container>
                    <Grid item xs={6}>
                      <FormDatePicker
                        label={t('general_Since')}
                        name="commissionFrom"
                        format="yyyy-MM-dd"
                        margin="dense"
                        size="medium"
                        openTo="date"
                        minDate={DateTime.local().minus({ year: 1 }).toISODate()}
                        maxDate={DateTime.local().minus({ day: 1 }).toISODate()}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormDatePicker
                        label={t('general_Until')}
                        name="commissionTo"
                        format="yyyy-MM-dd"
                        margin="dense"
                        openTo="date"
                        minDate={DateTime.local().minus({ year: 1 }).plus({ day: 1 }).toISODate()}
                        maxDate={DateTime.local().toISODate()}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={2} />
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      store.downloadCommission(formikProps.values.commissionFrom, formikProps.values.commissionTo)
                    }
                  >
                    {t('general_generate')}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={7}>
                  <Typography>
                    <strong>{t('backOffice_accounts_title')}</strong>
                  </Typography>
                </Grid>
                <Grid container item xs={2} spacing={0} alignItems="center" />
                <Grid item>
                  <Button variant="contained" color="primary" onClick={() => store.downloadAccounts(admin)}>
                    {t('general_generate')}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={7}>
                  <Typography>
                    <strong>{t('backOffice_unprocessedTransfers')}</strong>
                  </Typography>
                </Grid>
                <Grid container item xs={2} spacing={0} alignItems="center" />
                <Grid item>
                  <Button variant="contained" color="primary" onClick={() => store.downloadUnprocessedTransfers()}>
                    {t('general_generate')}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} container alignItems="center">
                <Grid item xs={7}>
                  <Typography>
                    <strong>{t('backOffice_portfolios')}</strong>
                  </Typography>
                </Grid>
                <Grid container item xs={2} spacing={0} alignItems="center" />
                <Grid item>
                  <Button variant="contained" color="primary" onClick={() => store.downloadPortfolios()}>
                    {t('general_generate')}
                  </Button>
                </Grid>
              </Grid>

            </Grid>
          )}
        </Formik>

        <ConfirmDialog noHeader isOpen={successDialog} confirm={store.hideSuccessDialog}>
          {t('backOffice_dataSaved')}
        </ConfirmDialog>

        <ConfirmDialog noHeader isOpen={confirmDialog} confirm={store.executeOrder} close={store.hideConfirmDialog}>
          {t('backOffice_executeOrderConfirm')}
        </ConfirmDialog>

        <GenerationDialog store={store} />
      </Background>
    </Container>
  );
};

export default compose(inject('rootStore'), withRouter, observer)(Csv);
